import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd, breadcrumbJsonLd } from "../lib/seo";
import { CONTACT_EMAIL, LEGAL_LAST_UPDATED, OPERATOR_ENTITY } from "../lib/legal";

export function PrivacyPage() {
  usePageMeta({
    title: "Privacy Policy",
    description:
      "What data MarketOnBase collects, why, who it is shared with, how long it is kept, and what you can ask us to do with it — including the parts of blockchain data that cannot be deleted.",
    path: "/privacy",
  });

  useJsonLd(
    useMemo(
      () =>
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Privacy Policy", path: "/privacy" },
        ]),
      [],
    ),
  );

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <h1>Privacy Policy</h1>
        <p className="muted">Last updated: {LEGAL_LAST_UPDATED}</p>
      </div>

      <div className="alert alert--warning" role="note">
        <strong>In use and applied on MarketOnBase.com</strong>
        <p>
          08/01/2026
        </p>
      </div>

      <section className="stack">
        <h2>1. The short version</h2>
        <p>
          We collect the minimum needed to run a marketplace: who you are as an account, what you
          listed or bought, and the technical records needed to keep the service working and to
          stop abuse. We don&apos;t sell your data, we don&apos;t run advertising trackers, and we
          never see your private keys.
        </p>
        <p>
          The one thing worth understanding up front: <strong>blockchain records are public and
          permanent.</strong> Your wallet address and every on-chain transaction it makes are
          visible to anyone, forever, and neither we nor anyone else can delete them.
        </p>
      </section>

      <section className="stack">
        <h2>2. What we collect</h2>
        <h3>Account information</h3>
        <ul>
          <li>
            <strong>Email address</strong>, from your Coinbase sign-in. Used to identify your
            account and, if needed, to contact you about a transaction. It is never shown to other
            users.
          </li>
          <li>
            <strong>A Coinbase user identifier</strong> linking your account to your Coinbase
            Developer Platform identity.
          </li>
          <li>
            <strong>Your wallet address(es)</strong> on Base, and the type of wallet.
          </li>
          <li>
            <strong>A display name</strong>, if you choose to set one. This is public — it is
            shown to counterparties on your listings and orders. Don&apos;t use your real name
            unless you intend it to be public.
          </li>
        </ul>

        <h3>Marketplace activity</h3>
        <ul>
          <li>Listings you create: title, description, price, and the files you upload.</li>
          <li>
            Orders you place or receive: amounts, fees, timestamps, onchain order and transaction
            identifiers, and a snapshot of the counterparty&apos;s display name at the time.
          </li>
          <li>
            Disputes you open or respond to: your written claim or rebuttal and any evidence files
            you attach.
          </li>
        </ul>

        <h3>Technical and security records</h3>
        <ul>
          <li>
            <strong>IP address</strong>, used for rate limiting and to detect bulk automated
            account creation. An IP address is recorded on the audit record created when a new
            account is first provisioned.
          </li>
          <li>
            <strong>Server logs</strong> covering requests to the service, including request
            identifiers and error details.
          </li>
          <li>
            <strong>Error reports</strong>, which may incidentally include technical context about
            what you were doing when something broke.
          </li>
          <li>
            <strong>File metadata</strong> for uploads: filename, declared type, size, and a
            content hash.
          </li>
        </ul>

        <h3>What we do not collect</h3>
        <ul>
          <li>
            <strong>Your private keys or seed phrase.</strong> Your wallet is a Coinbase-managed
            Smart Account; we cannot sign transactions on your behalf.
          </li>
          <li>
            <strong>Payment card or bank details.</strong> The service settles in USDC onchain and
            never touches traditional payment rails.
          </li>
          <li>
            <strong>Advertising or cross-site tracking data.</strong> There are no third-party
            advertising trackers on this site.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>3. Why we use it</h2>
        <ul>
          <li>To create and operate your account and show you your listings and orders.</li>
          <li>To process checkouts and produce the transactions you sign.</li>
          <li>To scan uploaded files for malware before they can be sold.</li>
          <li>To administer the dispute process when one side raises a problem.</li>
          <li>
            To prevent fraud and abuse — rate limiting, blocking bulk automated signups, and
            investigating reports.
          </li>
          <li>To diagnose errors and keep the service running.</li>
          <li>To comply with legal obligations where they apply.</li>
        </ul>
      </section>

      <section className="stack">
        <h2>4. Who your data is shared with</h2>
        <p>We do not sell your personal data. We share it with the following categories only:</p>
        <ul>
          <li>
            <strong>Coinbase</strong> — provides authentication and the embedded wallet. Your
            interactions with the wallet are subject to Coinbase&apos;s own privacy policy.
          </li>
          <li>
            <strong>Our infrastructure providers</strong> — hosting, database, object storage for
            uploaded files, and bot-protection on sign-in. These process data on our behalf under
            their own terms.
          </li>
          <li>
            <strong>An error-monitoring provider</strong> — receives crash and error reports.
          </li>
          <li>
            <strong>The other party to a transaction</strong> — a buyer and seller can see each
            other&apos;s display name and wallet address for their shared order, and each
            other&apos;s submissions in a dispute.
          </li>
          <li>
            <strong>Law enforcement or regulators</strong> — where we are legally required to
            respond, or where necessary to investigate fraud or protect users.
          </li>
          <li>
            <strong>The public blockchain</strong> — see the next section.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>5. Blockchain data is public and permanent</h2>
        <p>
          Funding, release, dispute, and settlement all happen onchain on Base. This means
          your wallet address, the amounts involved, the counterparty address, and the timing of
          every transaction are <strong>permanently public</strong> and readable by anyone, with or
          without an account here.
        </p>
        <p>
          Nobody can edit or erase that data — not us, not you, and not Coinbase. A request to
          delete your account cannot and will not remove anything already written to the
          blockchain. If a wallet address is ever linked to your identity elsewhere, its entire
          history here becomes linkable too. Consider that before transacting.
        </p>
      </section>

      <section className="stack">
        <h2>6. How long we keep it</h2>
        <ul>
          <li>
            <strong>Account records</strong> — for as long as your account exists, and afterwards
            where needed for legal, accounting, or dispute-defence reasons.
          </li>
          <li>
            <strong>Order and dispute records</strong> — retained after settlement, because they
            are the record of a financial transaction and may be needed to resolve a later claim.
          </li>
          <li>
            <strong>Uploaded files</strong> — retained while the listing is active and while buyers
            remain entitled to download what they purchased. Files that fail a malware scan or
            whose upload is abandoned are cleaned up.
          </li>
          <li>
            <strong>Technical logs and error reports</strong> — retained for a limited operational
            period.
          </li>
          <li>
            <strong>Onchain data</strong> — permanent, as described above.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>7. Your choices and rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct, delete, restrict, or
          port your personal data, and to object to certain processing. To make a request, contact
          us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <p>Two honest limits on those rights here:</p>
        <ul>
          <li>
            We cannot delete blockchain records, because we do not control the blockchain.
          </li>
          <li>
            We may need to retain order and dispute records even after an account is closed, where
            they are needed to establish, exercise, or defend legal claims — for example if the
            other side of a transaction raises an issue later.
          </li>
        </ul>
        <p>
          You can change or remove your public display name at any time from your{" "}
          <Link to="/profile">profile page</Link>. Orders already placed keep the name that was in
          effect at the time, because the counterparty is entitled to an accurate record of who
          they dealt with.
        </p>
      </section>

      <section className="stack">
        <h2>8. Security</h2>
        <p>
          Uploaded files go into private storage that is not publicly reachable and are scanned for
          malware before they can be sold. Access to files is granted through short-lived signed
          links issued only to a buyer who is actually entitled to that file. Transport is
          encrypted. Your funds are held by a smart contract rather than by us, which means a
          compromise of our systems does not by itself put held funds at risk.
        </p>
        <p>
          No system is perfectly secure, and we don&apos;t claim otherwise. This service has not
          completed an independent security audit.
        </p>
      </section>

      <section className="stack">
        <h2>9. Children</h2>
        <p>
          This service is not intended for anyone under 18, and we do not knowingly collect data
          from children. If you believe a child has created an account, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will remove it.
        </p>
      </section>

      <section className="stack">
        <h2>10. International transfers</h2>
        <p>
          Our infrastructure providers may process and store data in countries other than your own,
          including the United States. Where required, we rely on the safeguards those providers
          put in place for such transfers.
        </p>
      </section>

      <section className="stack">
        <h2>11. Changes to this policy</h2>
        <p>
          We may update this policy as the service changes. The &ldquo;last updated&rdquo; date at
          the top reflects the most recent change. Continuing to use the service after a change
          means you accept the updated policy.
        </p>
      </section>

      <section className="stack">
        <h2>12. Contact</h2>
        <p>
          Questions about this policy or your data can be sent to{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>, addressed to {OPERATOR_ENTITY}.
        </p>
        <p className="text-sm muted">
          See also our <Link to="/terms">Terms of Service</Link>.
        </p>
      </section>
    </article>
  );
}
